import { z } from 'zod';
import {
  SafetyNoticeSchema,
  acknowledgementId,
  wordingChanged,
  type NoticeAudience,
  type SafetyAcknowledgement,
  type SafetyNotice,
} from '../../domain/entities/safety-notice.ts';
import { newId } from '../../domain/id.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { FunctionsPort } from '../../ports/functions-port.ts';
import type { Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * Publishing, rewording and retiring a safety notice, and reading the ones
 * a person still has to acknowledge.
 *
 * The notices themselves are an ordinary client write gated on `settings:
 * edit` — they are prose somebody types, and nothing is derived from them.
 * The **acknowledgement** is not: it goes through a callable, because a
 * client that could write one could acknowledge on somebody else's behalf,
 * or claim an older revision to dodge the wording that is actually up. See
 * `acknowledgeSafetyNotice`.
 */
export class SafetyNoticeError extends Error {
  readonly reason: 'invalid' | 'retired';

  constructor(reason: 'invalid' | 'retired', message: string) {
    super(message);
    this.name = 'SafetyNoticeError';
    this.reason = reason;
  }
}

export const NoticeInputSchema = z.object({
  title: z.string().trim().min(1, 'A notice needs a title').max(200),
  body: z.string().trim().min(1, 'A notice with no wording asks nothing').max(5_000),
  audience: z.array(z.enum(['internal', 'sub'])).min(1, 'Choose who has to read it'),
});
export type NoticeInput = z.infer<typeof NoticeInputSchema>;

export interface NoticeContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly uid: string;
}

type ReadContext = Pick<NoticeContext, 'data' | 'companyId'>;

function repo(context: ReadContext) {
  return context.data.collection<SafetyNotice>({ companyId: context.companyId }, 'safetyNotices');
}

/** Every notice, newest first — the settings screen shows retired ones too. */
export async function listSafetyNotices(context: ReadContext): Promise<SafetyNotice[]> {
  const page = await repo(context).list({
    orderBy: [{ field: 'publishedAt', direction: 'desc' }],
  });
  return [...page.items];
}

export function subscribeToSafetyNotices(
  context: ReadContext,
  onChange: (notices: readonly SafetyNotice[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return repo(context).subscribe(
    { orderBy: [{ field: 'publishedAt', direction: 'desc' }] },
    (page) => onChange(page.items),
    onError,
  );
}

/**
 * Put a notice up.
 *
 * Published in the same act as it is written — there is no draft, for the
 * reasons on `SafetyNoticeSchema`. `revision` starts at 1.
 */
export async function publishSafetyNotice(
  context: NoticeContext,
  input: NoticeInput,
  id = newId(),
): Promise<SafetyNotice> {
  const parsed = NoticeInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SafetyNoticeError('invalid', parsed.error.issues[0]!.message);
  }
  const now = context.clock.now().toISOString();
  const notice = SafetyNoticeSchema.parse({
    id,
    companyId: context.companyId,
    title: parsed.data.title,
    body: parsed.data.body,
    audience: parsed.data.audience as NoticeAudience[],
    revision: 1,
    publishedAt: now,
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  });
  await repo(context).create(notice);
  return notice;
}

/**
 * Change a notice, bumping the revision only if the **wording** moved.
 *
 * Widening the audience does not invalidate anybody's acknowledgement:
 * the people who already read it read the same words. Changing a letter of
 * the title or body does, which is the decision this feature turns on —
 * see `SafetyNoticeSchema`.
 *
 * A **retired** notice is not editable. It is a record of what was up and
 * when; rewriting one would rewrite history, and putting a new notice up
 * is one button away.
 */
export async function reviseSafetyNotice(
  context: NoticeContext,
  id: string,
  input: NoticeInput,
): Promise<SafetyNotice> {
  const parsed = NoticeInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SafetyNoticeError('invalid', parsed.error.issues[0]!.message);
  }
  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('safetyNotices', id);
  if (existing.retiredAt) {
    throw new SafetyNoticeError(
      'retired',
      'This notice has been taken down. Put a new one up instead of rewriting it.',
    );
  }

  const reworded = wordingChanged(existing, parsed.data);
  const now = context.clock.now().toISOString();
  const next: SafetyNotice = {
    ...existing,
    title: parsed.data.title,
    body: parsed.data.body,
    audience: parsed.data.audience as NoticeAudience[],
    revision: reworded ? existing.revision + 1 : existing.revision,
    updatedAt: now,
    updatedBy: context.uid,
  };
  await repo(context).update(id, next);
  return next;
}

/** Take it down. Nothing is deleted — an acknowledged notice is a record. */
export async function retireSafetyNotice(
  context: NoticeContext,
  id: string,
): Promise<SafetyNotice> {
  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('safetyNotices', id);
  const now = context.clock.now().toISOString();
  const next: SafetyNotice = {
    ...existing,
    retiredAt: existing.retiredAt ?? now,
    updatedAt: now,
    updatedBy: context.uid,
  };
  await repo(context).update(id, next);
  return next;
}

/**
 * Which of these notices this person has already acknowledged.
 *
 * **One `get` per notice at a known path**, not a query, which is what
 * `acknowledgementId` is for. A query would need a rule allowing a member
 * to list a collection that holds everybody's acknowledgements, and a
 * `list` whose rule reads `resource.data` returns everything matched or
 * fails outright — so the read is by id and the rule stays "your own".
 *
 * A failed read is treated as **not acknowledged**, deliberately: showing
 * the notice again is the safe direction to be wrong in.
 */
export async function acknowledgedIds(
  context: ReadContext,
  uid: string,
  notices: readonly Pick<SafetyNotice, 'id' | 'revision'>[],
): Promise<Set<string>> {
  const found = new Set<string>();
  for (const notice of notices) {
    const id = acknowledgementId(uid, notice.revision);
    try {
      const entry = await context.data
        .collection<SafetyAcknowledgement>(
          { companyId: context.companyId, record: { collection: 'safetyNotices', id: notice.id } },
          'safetyAcknowledgements',
        )
        .get(id);
      if (entry) found.add(id);
    } catch {
      // Not acknowledged, as far as this page is concerned.
    }
  }
  return found;
}

/**
 * Who has acknowledged one notice, live.
 *
 * A subscription rather than a read, because the figure it feeds is the
 * one somebody sits and watches: "how many of my people have read what is
 * up now". A one-shot read was the first version, and it did not move when
 * the reader acknowledged the notice themselves from the splash sitting
 * over the same page — which is the first thing anybody does.
 */
export function subscribeToAcknowledgements(
  context: ReadContext,
  noticeId: string,
  onChange: (entries: readonly SafetyAcknowledgement[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return context.data
    .collection<SafetyAcknowledgement>(
      { companyId: context.companyId, record: { collection: 'safetyNotices', id: noticeId } },
      'safetyAcknowledgements',
    )
    .subscribe({}, (page) => onChange(page.items), onError);
}

/** Who has acknowledged one notice, for a caller that wants it once. */
export async function listAcknowledgements(
  context: ReadContext,
  noticeId: string,
): Promise<SafetyAcknowledgement[]> {
  const page = await context.data
    .collection<SafetyAcknowledgement>(
      { companyId: context.companyId, record: { collection: 'safetyNotices', id: noticeId } },
      'safetyAcknowledgements',
    )
    .list();
  return [...page.items];
}

/**
 * Acknowledge a notice.
 *
 * A callable, and the reason is the only one that matters here: the
 * **revision is stamped by the server**. A client that could choose it
 * would acknowledge revision 1 of a notice now on revision 3 and read as
 * compliant, which is the one thing this record must not permit. The uid
 * comes from the token for the same reason.
 *
 * Idempotent: the document id is derived from the uid and the revision, so
 * a double-pressed button is one acknowledgement with its original
 * timestamp. The first press is the interesting fact.
 */
export async function acknowledgeSafetyNotice(
  functions: FunctionsPort,
  input: { readonly companyId: string; readonly noticeId: string },
): Promise<{ readonly revision: number; readonly at: string }> {
  return functions.call('acknowledgeSafetyNotice', input);
}

/**
 * Chase the people who have not acknowledged a notice (P5-23, SCRUM-215).
 *
 * A callable because it writes into other people's notification trees,
 * which a client cannot do and should not be able to. The server decides
 * who is outstanding, from the revision that is current when the button is
 * pressed — a client sending a list of uids could chase people who had
 * already acknowledged, or miss somebody.
 */
export async function chaseSafetyNotice(
  functions: FunctionsPort,
  input: { readonly companyId: string; readonly noticeId: string },
): Promise<{ readonly chased: number; readonly revision: number }> {
  return functions.call('chaseSafetyNotice', input);
}
